import { type Component, createSignal, For, onCleanup, onMount } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import type { FabMenuPropsType } from '~/types/FabMenu'

const FAB_SIZE = 56
const EDGE = 8
const DRAG_THRESHOLD = 4

/**
 * Menú flotante estilo Material You: un FAB que al pulsarse despliega pills
 * de acciones apilados verticalmente, con backdrop y animación escalonada.
 * Se puede arrastrar: al soltarlo se magnetiza al borde más cercano y se
 * mantiene dentro del viewport. Diseñado para móviles donde las acciones
 * del header no caben.
 */
const FabMenu: Component<FabMenuPropsType> = props => {
  const [isOpen, setIsOpen] = createSignal(false)
  const [ready, setReady] = createSignal(false)
  const [dragging, setDragging] = createSignal(false)
  const [posX, setPosX] = createSignal(0)
  const [posY, setPosY] = createSignal(0)

  let fabRef: HTMLButtonElement | undefined
  let dragStartX = 0
  let dragStartY = 0
  let baseX = 0
  let baseY = 0
  let hasMoved = false

  const close = () => setIsOpen(false)

  const handleKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen()) close()
  }

  onMount(() => {
    document.addEventListener('keydown', handleKey)
    setPosX(innerWidth - FAB_SIZE - EDGE)
    setPosY(innerHeight - FAB_SIZE - 20)
    setReady(true)
  })
  onCleanup(() => document.removeEventListener('keydown', handleKey))

  const clampY = (v: number) => Math.min(Math.max(v, EDGE), innerHeight - FAB_SIZE - EDGE)

  /** Lado en el que está el FAB: decide el magnetismo y la alineación de los pills. */
  const side = () => (posX() + FAB_SIZE / 2 < innerWidth / 2 ? 'left' : 'right')

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return
    const rect = fabRef?.getBoundingClientRect()
    if (!rect) return
    dragStartX = e.clientX
    dragStartY = e.clientY
    baseX = rect.left
    baseY = rect.top
    hasMoved = false
    fabRef?.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: PointerEvent) => {
    // Sin botón presionado (hover, scroll, acercamiento del puntero) no es un
    // arrastre: un move previo al pointerdown activaría dragging y bloquearía
    // el toggle del menú en el siguiente tap.
    if (e.buttons === 0) return
    if (!hasMoved) {
      const dx = e.clientX - dragStartX
      const dy = e.clientY - dragStartY
      if (Math.hypot(dx, dy) <= DRAG_THRESHOLD) return
      hasMoved = true
      setDragging(true)
      close()
    }
    const x = Math.min(Math.max(baseX + (e.clientX - dragStartX), EDGE), innerWidth - FAB_SIZE - EDGE)
    const y = clampY(baseY + (e.clientY - dragStartY))
    setPosX(x)
    setPosY(y)
  }

  const onPointerUp = () => {
    if (hasMoved) {
      // Magnetismo: al soltar se pega al borde más cercano, manteniendo la
      // altura; la transición CSS anima el movimiento hasta anclarse.
      const targetX = side() === 'left' ? EDGE : innerWidth - FAB_SIZE - EDGE
      setPosX(targetX)
      setPosY(clampY(posY()))
      setDragging(false)
    } else if (!dragging()) {
      setIsOpen(prev => !prev)
    }
    hasMoved = false
  }

  const pillsBelow = () => posY() < 220

  return (
    <>
      {/* Backdrop */}
      <div class={twMerge('fixed inset-0 bg-black/30 z-40 transition-opacity duration-300', isOpen() ? 'opacity-100' : 'opacity-0 pointer-events-none')} onClick={close} aria-hidden="true" />

      {/* FAB + pills, anclados a la posición actual */}
      <div class={twMerge('fixed z-50', ready() ? '' : 'bottom-5 right-5')} style={ready() ? { left: `${posX()}px`, top: `${posY()}px` } : undefined}>
        <div class="relative">
          {/* Pills de acciones: encima del FAB, alineados según el lado */}
          <div
            class={twMerge(
              'absolute flex flex-col gap-3 transition-all duration-300 ease-out',
              pillsBelow() ? 'top-full mt-3' : 'bottom-full mb-3',
              side() === 'left' ? 'left-0 items-start' : 'right-0 items-end',
              isOpen() ? 'opacity-100 translate-y-0' : 'opacity-0 pointer-events-none'
            )}
          >
            <For each={props.items}>
              {(item, i) => (
                <button
                  type="button"
                  onClick={() => {
                    item.onClick()
                    close()
                  }}
                  class={twMerge(
                    'flex items-center gap-3 pl-4 pr-1.5 py-1.5 rounded-full bg-surface shadow-lg border border-border cursor-pointer whitespace-nowrap',
                    'active:scale-95 transition-all duration-200 ease-out',
                    isOpen() ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  )}
                  style={{ 'transition-delay': isOpen() ? `${i() * 50}ms` : '0ms' }}
                >
                  <span class="text-sm font-medium text-foreground">{item.label}</span>
                  <div class="size-12 rounded-full bg-primary-soft flex items-center justify-center shrink-0">
                    <Icon icon={item.icon} size={28} class="text-primary" />
                  </div>
                </button>
              )}
            </For>
          </div>

          {/* FAB: arrastrable, se magnetiza al borde más cercano al soltar */}
          <button
            ref={fabRef}
            type="button"
            aria-label={isOpen() ? 'Cerrar acciones' : 'Acciones'}
            aria-expanded={isOpen()}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            class={twMerge(
              'size-14 rounded-full bg-primary-500 text-white shadow-lg shadow-primary-500/30 flex items-center justify-center cursor-pointer touch-none select-none',
              'hover:shadow-xl hover:scale-105 active:scale-95',
              dragging() ? 'transition-none' : 'transition-all duration-300'
            )}
          >
            <Icon icon="material-symbols-light:add" size={28} class={twMerge('transition-transform duration-300', isOpen() ? 'rotate-45' : '')} />
          </button>
        </div>
      </div>
    </>
  )
}

export default FabMenu
